import { useState } from "react";
import moment from "moment";
import {
  AVAILABILITY_LOG_TYPE,
  BUCKET_DROPPABLE_CONTAINER_ID,
  COLUMN_GAP,
  COLUMN_HEIGHT,
  COLUMN_WIDTH,
  EARLIEST_TIME,
  HEIGHT_OF_ONE_HOUR,
  ONE_HOUR,
  SNAP_INTERVAL_IN_HOURS,
  STANDARD_DATE_FORMAT,
  WEEK_FORMAT_SHORT,
} from "@miplatform/constants";
import {
  createUUID,
  getDateWithAddedTime,
  getDateWithNewHours,
  getDeactivatedIntervals,
  getDurationInHours,
  getPosition,
  isSameDay,
  snapAdjusted,
  toHour,
  toSnappedTime,
} from "@miplatform/utils";
import type { DragEndEvent, DragStartEvent, Modifier } from "@dnd-kit/core";
import type {
  AvailabilityLog,
  DateInRange,
  DraggingEvent,
  PositionedEvent,
  SchedulerColumnTuple,
  SchedulerEvent,
  SchedulerUpdates,
  TimeInterval,
  UserWithAvailability,
} from "../types";

export const useDaysViewViewModel = ({
  onEventUpdate,
  onEventCreate,
  zoom = 1,
  currentTechnicianData,
  intervals,
}: UseDaysViewViewModelParams): UseDaysViewViewModelReturn => {
  const [draggingEvent, setDraggingEvent] = useState<DraggingEvent | null>(
    null
  );
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [columns, setColumns] = useState<SchedulerColumnTuple[]>([]);

  const getDatesInRange = (
    startDate: string,
    endDate: string
  ): DateInRange[] => {
    const dates: DateInRange[] = [];

    for (
      const currentDate = moment(startDate).startOf("day").clone();
      currentDate.isSameOrBefore(endDate);
      currentDate.add(1, "d")
    ) {
      dates.push({
        day: currentDate.format(WEEK_FORMAT_SHORT).toUpperCase(),
        date: currentDate.format(STANDARD_DATE_FORMAT),
      });
    }

    return dates;
  };

  const dndSnap = (y: number): number => {
    const hour = y / HEIGHT_OF_ONE_HOUR + EARLIEST_TIME;

    return getPosition(snapAdjusted(hour, SNAP_INTERVAL_IN_HOURS * zoom));
  };

  const getColumnsFromData = ({
    data,
    datesInRange,
  }: GetColumnsFromDataParams): SchedulerColumnTuple[] => {
    return datesInRange?.map(({ date }): SchedulerColumnTuple => {
      const deactivatedIntervalsCoordinates =
        currentTechnicianData?.availabilityLog
          ?.filter((log: AvailabilityLog) => {
            if (log.type !== AVAILABILITY_LOG_TYPE.INACTIVE) {
              return false;
            }
            if (!log.from || !log.to) {
              return false;
            }
            return isSameDay(log.from, date);
          })
          .map((interval: AvailabilityLog) => {
            return {
              start: toHour(interval.from) * HEIGHT_OF_ONE_HOUR,
              end: toHour(interval.to) * HEIGHT_OF_ONE_HOUR,
            };
          });
      return [
        date,
        data
          .filter(({ displayStartTime }) => isSameDay(displayStartTime, date))
          .map((e): PositionedEvent => {
            const modifiedEvent: PositionedEvent = {
              ...e,
              uuid: createUUID(),
              y:
                toHour(
                  toSnappedTime(
                    getDateWithAddedTime(e.displayStartTime, {
                      h: e.delayedTime,
                    }),
                    "floor"
                  )
                ) * HEIGHT_OF_ONE_HOUR,
              duration: getDurationInHours(
                e.displayStartTime,
                e.displayEndTime
              ),
            };
            const startPosition = modifiedEvent.y;
            const endPosition =
              modifiedEvent.y +
              (modifiedEvent.duration + (modifiedEvent.extendedTime ?? 0)) *
                HEIGHT_OF_ONE_HOUR;
            const isOnInactiveInterval = deactivatedIntervalsCoordinates?.some(
              (interval) =>
                startPosition < interval.end && endPosition > interval.start
            );
            return { ...modifiedEvent, isOnInactiveInterval };
          }),
        getDeactivatedIntervals(
          currentTechnicianData?.availabilityLog,
          intervals,
          date
        ),
      ];
    });
  };

  const handleScroll =
    (ref: React.RefObject<HTMLDivElement | null>) =>
    (event: React.UIEvent<HTMLDivElement>): void => {
      if (ref.current) {
        ref.current.scrollLeft = event.currentTarget.scrollLeft;
      }
    };

  const handleDragStart = (event: DragStartEvent): void => {
    setDraggingEvent({ ...event.active.data.current, y: 0 } as DraggingEvent);
  };

  const handleDragCancel = (): void => {
    setDraggingEvent(null);
    setSelectedEventId("");
  };

  const snapModifier: Modifier = (args) => {
    const { transform, active } = args;

    if (active?.data?.current?.fromBucket) {
      return transform;
    }

    return {
      ...transform,
      x: snapAdjusted(transform.x, COLUMN_WIDTH + COLUMN_GAP),
      y: dndSnap(transform.y),
    };
  };

  const getUpdates = ({
    userId,
    newDate,
    duration,
    y,
  }: GetUpdatesParams): SchedulerUpdates => {
    const validY = Math.min(
      Math.max(dndSnap(y), 0),
      (COLUMN_HEIGHT - (duration || 1) * HEIGHT_OF_ONE_HOUR) * zoom
    );

    const time = validY / HEIGHT_OF_ONE_HOUR;
    const effectiveDuration = duration ?? 0;
    const dateString = newDate.format();
    const updates: SchedulerUpdates = {
      userId: userId ?? "",
      scheduledStartDate: getDateWithNewHours(dateString, time).format(),
      scheduledEndDate: getDateWithAddedTime(
        getDateWithNewHours(dateString, time + effectiveDuration),
        { s: -1 }
      ),
    };

    return updates;
  };

  const getUpdatesOnDragEnd = (
    event: DragEndEvent
  ): SchedulerUpdates | null => {
    const { active, over, delta } = event;
    const item = active.data.current as SchedulerEvent;

    if (!over) {
      return null;
    }

    if (item.fromBucket) {
      const translatedRect = active.rect.current.translated;
      if (!translatedRect) {
        return null;
      }
      const y = translatedRect.top - over.rect.top;
      const updates = getUpdates({
        newDate: moment(over.id),
        duration: item?.expectedDuration
          ? item.expectedDuration / ONE_HOUR
          : null,
        y,
      });

      return updates;
    }

    const y = (item?.y || 0) + delta.y / zoom;
    const updates = getUpdates({
      userId: item.assignedUserId,
      newDate: moment(over.id),
      duration: item.duration ?? null,
      y,
    });

    return updates;
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    handleDragCancel();
    if (!event.over || event.over?.id === BUCKET_DROPPABLE_CONTAINER_ID) {
      return;
    }

    const updates = getUpdatesOnDragEnd(event);
    if (!updates) {
      return;
    }

    const item = event.active.data.current as SchedulerEvent;

    const handler = item.fromBucket ? onEventCreate : onEventUpdate;
    handler?.(item, updates);
  };

  const prepareColumns = ({
    data,
    datesInRange,
  }: GetColumnsFromDataParams): void => {
    setColumns(getColumnsFromData({ data, datesInRange }));
  };

  return {
    columns,
    draggingEvent,
    selectedEventId,
    setSelectedEventId,
    getDatesInRange,
    handleScroll,
    handleDragStart,
    handleDragCancel,
    snapModifier,
    prepareColumns,
    handleDragEnd,
  };
};

interface GetColumnsFromDataParams {
  data: SchedulerEvent[];
  datesInRange: DateInRange[];
}

interface GetUpdatesParams {
  userId?: string;
  newDate: moment.Moment;
  duration: number | null;
  y: number;
}

export interface UseDaysViewViewModelParams {
  onEventUpdate?: (item: SchedulerEvent, updates: SchedulerUpdates) => void;
  onEventCreate?: (item: SchedulerEvent, updates: SchedulerUpdates) => void;
  zoom?: number;
  currentTechnicianData?: UserWithAvailability;
  intervals: TimeInterval[];
}

export interface UseDaysViewViewModelReturn {
  columns: SchedulerColumnTuple[];
  draggingEvent: DraggingEvent | null;
  selectedEventId: string;
  setSelectedEventId: React.Dispatch<React.SetStateAction<string>>;
  getDatesInRange: (startDate: string, endDate: string) => DateInRange[];
  handleScroll: (
    ref: React.RefObject<HTMLDivElement | null>
  ) => (event: React.UIEvent<HTMLDivElement>) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragCancel: () => void;
  snapModifier: Modifier;
  prepareColumns: (params: GetColumnsFromDataParams) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}
